/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import { ArrowRight, RefreshCw } from "lucide-react";

import { Endpoint, EndpointUtils, StorageMap } from "@src/@types/Endpoint";
import { ProviderTypes } from "@src/@types/Providers";
import Schedule from "@src/components/modules/TransferModule/Schedule";
import WizardBreadcrumbs from "@src/components/modules/WizardModule/WizardBreadcrumbs";
import WizardEndpointList from "@src/components/modules/WizardModule/WizardEndpointList";
import WizardInstances from "@src/components/modules/WizardModule/WizardInstances";
import WizardNetworks, {
  WizardNetworksChangeObject,
} from "@src/components/modules/WizardModule/WizardNetworks";
import WizardOptions from "@src/components/modules/WizardModule/WizardOptions";
import WizardScripts from "@src/components/modules/WizardModule/WizardScripts";
import WizardStorage from "@src/components/modules/WizardModule/WizardStorage";
import WizardSummary from "@src/components/modules/WizardModule/WizardSummary";
import WizardExecuteOptions from "@src/components/modules/WizardModule/WizardExecuteOptions";
import WizardGuestAccess, {
  guestAccessReady,
} from "@src/components/modules/WizardModule/WizardGuestAccess";
import WizardPrerequisites, {
  prerequisitesReady,
} from "@src/components/modules/WizardModule/WizardPrerequisites/WizardPrerequisites";
import WizardType from "@src/components/modules/WizardModule/WizardType";
import { T9 } from "@src/components/modules/WizardModule/WizardKit";
import WizardReadiness, {
  readinessReady,
} from "@src/components/modules/WizardModule/WizardReadiness";
import BandwidthEditor from "@src/components/modules/TransferModule/Bandwidth";
import {
  BANDWIDTH_KEYS,
  LIMIT_KEY,
  SCHEDULE_KEY,
  hasErrors as bandwidthHasErrors,
  readBandwidth,
  validateBandwidth,
} from "@src/components/modules/TransferModule/Bandwidth/bandwidth";
import wizardStore from "@src/stores/WizardStore";
import { ThemePalette } from "@src/components/Theme";
import Button from "@src/components/ui/Button";
import LoadingButton from "@src/components/ui/LoadingButton";
import Switch from "@src/components/ui/Switch";
import { REMOTE_ACCESS_KEYS } from "@src/components/modules/WizardModule/WizardGuestAccess/WizardRemoteAccess";
import {
  deploymentFields,
  executeOptionsWithExecuteNow,
  providerTypes,
  wizardPages,
} from "@src/constants";
import endpointStore from "@src/stores/EndpointStore";
import instanceStore from "@src/stores/InstanceStore";
import minionPoolStore from "@src/stores/MinionPoolStore";
import networkStore from "@src/stores/NetworkStore";
import notificationStore from "@src/stores/NotificationStore";
import providerStore from "@src/stores/ProviderStore";
import configLoader from "@src/utils/Config";

import type { WizardData, WizardPage } from "@src/@types/WizardData";
import type {
  Instance,
  InstanceScript,
  UserScriptTarget,
} from "@src/@types/Instance";
import type { Field } from "@src/@types/Field";
import type { Schedule as ScheduleType } from "@src/@types/Schedule";

/*
 * The wizard's frame, as in the Ops Console's create pages: the page title, the steps in a left rail
 * that stays in view, and the step in a white card whose content flows with the page (one scroll,
 * the workspace's) with its actions pinned to the bottom of the screen.
 */
const Wrapper = styled.div<any>`
  box-sizing: border-box;
  max-width: 1280px;
  min-height: 100%;
  min-height: calc(100vh - 56px);
  margin: 0 auto;
  padding: 24px 24px 0 24px;
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  column-gap: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const PageTitle = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`;
const PageTitleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const H1 = styled.h1`
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: ${T9.slate900};
`;
const PageSub = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${T9.slate500};
  font-variant-numeric: tabular-nums;
`;
const Route = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${T9.slate200};
  border-radius: 6px;
  background: var(--t9-bg-surface);
  font-size: 12px;
  color: ${T9.slate500};
`;
const RouteArrow = styled(ArrowRight)`
  color: ${T9.slate400};
  flex-shrink: 0;
`;
const Rail = styled.nav`
  position: sticky;
  top: 24px;
  align-self: start;
  padding: 16px;
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px;

  @media (max-width: 1100px) {
    display: none;
  }
`;
const Card = styled.section`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px 8px 0 0;
  border-bottom: none;
`;
const Header = styled.div<any>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 16px 24px;
  border-bottom: 1px solid ${T9.slate100};
`;
const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const HeaderLabel = styled.h2<any>`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: ${T9.slate900};
`;
const HeaderDescription = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${T9.slate500};
`;
const HeaderReload = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 30px;
  padding: 0 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${T9.slate600};
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    color: ${T9.brand700};
    border-color: ${T9.brand300};
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
`;
const Body = styled.div<any>`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  padding: 20px 24px 24px 24px;
  min-width: 0;
`;
/* 5-step flow: a page shows several steps, one section each. */
const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
  min-width: 0;
`;
const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  & + & {
    border-top: 1px solid ${T9.slate200};
    padding-top: 24px;
  }
`;
const SectionTitle = styled.h2`
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${T9.slate500};
`;
const SectionBody = styled.div`
  display: flex;
  justify-content: center;
  min-width: 0;
`;
const ModeSwitch = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${T9.slate900};
  white-space: nowrap;
`;
const ModeHint = styled.span`
  font-weight: 400;
  color: ${T9.slate500};
`;
const TitleRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;
const ActionBar = styled.div<any>`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 0 0 8px 8px;
  box-shadow: 0 -4px 12px -8px rgba(15, 23, 42, 0.18);
  margin-bottom: 24px;
`;
const ActionHint = styled.div`
  flex-grow: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
`;
const CancelLink = styled.button`
  height: 34px;
  padding: 0 12px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${T9.slate600};
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    color: ${T9.slate900};
    background: ${T9.slate100};
  }
`;
const SourceOptionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;
const BandwidthSection = styled.div`
  margin: 8px 0 0 0;
  padding-top: 24px;
  border-top: 1px solid ${ThemePalette.grayscale[0]};
`;
type Props = {
  page: { id: string; title: string; description?: string; sections?: string[] };
  /** 5-step flow (false) or all 14 steps (true). */
  advanced?: boolean;
  onAdvancedChange?: (advanced: boolean) => void;
  type: "replica" | "migration";
  nextButtonDisabled: boolean;
  providerStore: typeof providerStore;
  instanceStore: typeof instanceStore;
  networkStore: typeof networkStore;
  endpointStore: typeof endpointStore;
  minionPoolStore: typeof minionPoolStore;
  wizardData: WizardData;
  schedules: ScheduleType[];
  storageMap: StorageMap[];
  onStorageReloadClick: () => void;
  defaultStorage: { value: string | null; busType?: string | null } | undefined;
  hasStorageMap: boolean;
  hasSourceOptions: boolean;
  pages: WizardPage[];
  uploadedUserScripts: InstanceScript[];
  showLoadingButton: boolean;
  onTypeChange: (isReplicaChecked: boolean | null) => void;
  onBackClick: () => void;
  onNextClick: () => void;
  onSourceEndpointChange: (endpoint: Endpoint) => void;
  onTargetEndpointChange: (endpoint: Endpoint) => void;
  onAddEndpoint: (provider: ProviderTypes, fromSource: boolean) => void;
  onInstancesSearchInputChange: (searchText: string) => void;
  onInstancesReloadClick: () => void;
  onInstanceClick: (instance: Instance) => void;
  onInstancePageClick: (page: number) => void;
  onDestOptionsChange: (
    field: Field,
    value: any,
    parentFieldName?: string,
  ) => void;
  onSourceOptionsChange: (
    field: Field,
    value: any,
    parentFieldName?: string,
  ) => void;
  onNetworkChange: (changeObject: WizardNetworksChangeObject) => void;
  onStorageChange: (mapping: StorageMap) => void;
  onDefaultStorageChange: (
    value: string | null,
    busType?: string | null,
  ) => void;
  onAddScheduleClick: (schedule: ScheduleType) => void;
  onScheduleChange: (scheduleId: string, schedule: ScheduleType) => void;
  onScheduleRemove: (scheudleId: string) => void;
  onContentRef: (ref: any) => void;
  onReloadOptionsClick: () => void;
  onReloadNetworksClick: () => void;
  onUserScriptsChange: (
    target: UserScriptTarget,
    scripts: InstanceScript[],
  ) => void;
  onTransferExecuteOptionsChange: (field: Field, value: any) => void;
  /** Leaves the wizard (shown from step 2 on; on step 1 Back already leaves). */
  onCancelClick?: () => void;
};
type TimezoneValue = "local" | "utc";
type State = {
  useAdvancedOptions: boolean;
  timezone: TimezoneValue;
};
@observer
class WizardPageContent extends React.Component<Props, State> {
  state: State = {
    useAdvancedOptions: false,
    timezone: "local",
  };

  optionsRef?: WizardOptions | null;

  componentDidMount() {
    this.props.onContentRef(this);
  }

  componentWillUnmount() {
    this.props.onContentRef(null);
  }

  getProvidersType(type: string) {
    return type === "source"
      ? providerTypes.SOURCE_TRANSFER
      : providerTypes.TARGET_TRANSFER;
  }

  getProviders(direction: string): ProviderTypes[] {
    const validProviders: {
      [provider in ProviderTypes]: true;
    } = {} as { [provider in ProviderTypes]: true };
    const providerType = this.getProvidersType(direction);
    const providersObject = this.props.providerStore.providers;

    if (!providersObject) {
      return [];
    }

    Object.keys(providersObject).forEach(provider => {
      const usableProvider = provider as ProviderTypes;
      if (
        providersObject[usableProvider].types.findIndex(
          t => t === providerType,
        ) > -1
      ) {
        validProviders[usableProvider] = true;
      }
    });

    return this.props.providerStore.providerNames.filter(
      p => validProviders[p],
    );
  }

  areOptionsLoading(type: "source" | "destination"): boolean {
    if (type === "source") {
      return (
        this.props.providerStore.sourceSchemaLoading ||
        this.props.providerStore.sourceOptionsPrimaryLoading ||
        this.props.minionPoolStore.loadingMinionPools
      );
    }
    return (
      this.props.providerStore.destinationSchemaLoading ||
      this.props.providerStore.destinationOptionsPrimaryLoading ||
      this.props.minionPoolStore.loadingMinionPools
    );
  }

  isNetworksPageValid() {
    if (
      this.props.networkStore.loading ||
      this.props.instanceStore.loadingInstancesDetails
    ) {
      return false;
    }

    const instances = this.props.instanceStore.instancesDetails;
    if (instances.length === 0) {
      return true;
    }

    if (instances.find(i => i.devices)) {
      if (instances.find(i => i.devices.nics && i.devices.nics.length > 0)) {
        return (
          this.props.wizardData.networks &&
          this.props.wizardData.networks.length > 0
        );
      }
      return true;
    }

    return false;
  }

  handleAdvancedOptionsToggle(useAdvancedOptions: boolean) {
    this.setState({ useAdvancedOptions });
  }

  handleTimezoneChange(timezone: TimezoneValue) {
    this.setState({ timezone });
  }

  get nextButtonDisabled() {
    if (this.props.nextButtonDisabled) {
      return true;
    }
    // A 5-step page is done when each of its steps is.
    const ids = this.props.page.sections || [this.props.page.id];
    return ids.some(id => this.stepIncomplete(id));
  }

  stepIncomplete(id: string): boolean {
    switch (id) {
      case "source":
        return !this.props.wizardData.source;
      case "target":
        return !this.props.wizardData.target;
      case "vms":
        return (
          !this.props.wizardData.selectedInstances ||
          !this.props.wizardData.selectedInstances.length
        );
      case "networks":
        return !this.isNetworksPageValid();
      case "guest-access":
        return !guestAccessReady(this.props.wizardData);
      case "prerequisites":
        return !prerequisitesReady(this.props.wizardData);
      case "readiness":
        return !readinessReady(this.props.wizardData);
      case "source-options":
        return this.hasBandwidthOptions && this.bandwidthInvalid;
      default:
        return false;
    }
  }

  /** Swift: the source schema has the Bandwidth options (bandwidth-and-readiness.md, 1.3). */
  get hasBandwidthOptions() {
    return this.props.providerStore.sourceSchema.some(f => BANDWIDTH_KEYS.includes(f.name));
  }

  get bandwidthInvalid() {
    const { limit, schedule } = readBandwidth(this.props.wizardData.sourceOptions);
    const raw = this.props.wizardData.sourceOptions?.[LIMIT_KEY];
    return bandwidthHasErrors(validateBandwidth(raw === undefined || raw === null ? limit : raw, schedule));
  }

  handleBandwidthChange(value: { limit: number | string; schedule: any[] }) {
    const numeric = value.limit === "" || Number.isNaN(Number(value.limit)) ? value.limit : Number(value.limit);
    wizardStore.updateData({
      sourceOptions: {
        ...(this.props.wizardData.sourceOptions || {}),
        [LIMIT_KEY]: numeric,
        [SCHEDULE_KEY]: value.schedule,
      },
    });
  }

  renderBandwidth() {
    if (!this.hasBandwidthOptions) {
      return null;
    }
    const options = this.props.wizardData.sourceOptions || {};
    const raw = options[LIMIT_KEY];
    const { schedule } = readBandwidth(options);
    return (
      <BandwidthSection>
        <BandwidthEditor
          limit={raw === undefined || raw === null ? 0 : raw}
          schedule={schedule}
          onChange={value => this.handleBandwidthChange(value)}
        />
      </BandwidthSection>
    );
  }

  handleNextClick() {
    let goNext = true;
    if (this.optionsRef?.highlightFields()) {
      goNext = false;
    }
    if (goNext) {
      this.props.onNextClick();
    } else {
      notificationStore.alert("Please fill the required fields", "error");
    }
  }

  get pageIndex() {
    return Math.max(0, this.props.pages.findIndex(p => p.id === this.props.page.id));
  }

  renderPageTitle() {
    const { source, target } = this.props.wizardData;
    const kind = this.props.type === "replica" ? "replica" : "migration";
    return (
      <PageTitle>
        <PageTitleText>
          <H1>New {kind}</H1>
          <PageSub>
            Step {this.pageIndex + 1} of {this.props.pages.length}
          </PageSub>
        </PageTitleText>
        <TitleRight>
          {this.props.onAdvancedChange ? (
            <ModeSwitch title="Advanced shows all 14 steps; off, the same settings fit in 5 steps with defaults.">
              <Switch
                noLabel
                height={16}
                checked={Boolean(this.props.advanced)}
                onChange={value => this.props.onAdvancedChange!(Boolean(value))}
              />
              Advanced
              <ModeHint>{this.props.advanced ? "all 14 steps" : "5 steps"}</ModeHint>
            </ModeSwitch>
          ) : null}
          {source || target ? (
            <Route aria-label="Source and target">
              <span>{source ? source.name : "Source"}</span>
              <RouteArrow size={14} strokeWidth={2} />
              <span>{target ? target.name : "Target"}</span>
            </Route>
          ) : null}
        </TitleRight>
      </PageTitle>
    );
  }

  renderHeader() {
    const pageId = this.props.page.id;
    const optionsReload = (type: "source" | "destination") => ({
      label: "Reload options",
      action: () => {
        this.props.onReloadOptionsClick();
      },
      tip: "Options may be cached. Reload them from the cloud.",
      visible: !this.areOptionsLoading(type),
    });
    const reloadPages: any = {
      "source-options": optionsReload("source"),
      "dest-options": optionsReload("destination"),
      networks: {
        label: "Reload networks",
        action: () => {
          this.props.onReloadNetworksClick();
        },
        tip: "Networks and VM details may be cached. Reload them from the cloud.",
        visible: !this.props.instanceStore.loadingInstancesDetails,
      },
    };
    // A 5-step page offers the reload of the first of its steps that has one.
    const reloadId = (this.props.page.sections || [pageId]).find(id => reloadPages[id]);
    const reload = reloadId ? reloadPages[reloadId] : null;
    return (
      <Header>
        <HeaderText>
          <HeaderLabel>{this.props.page.title}</HeaderLabel>
          {this.props.page.description ? (
            <HeaderDescription>{this.props.page.description}</HeaderDescription>
          ) : null}
        </HeaderText>
        {reload?.visible ? (
          <HeaderReload type="button" title={reload.tip} onClick={() => reload.action()}>
            <RefreshCw size={14} strokeWidth={2} />
            {reload.label}
          </HeaderReload>
        ) : null}
      </Header>
    );
  }

  renderBody() {
    const sections = this.props.page.sections;
    if (!sections) {
      return <Body>{this.renderStep(this.props.page.id)}</Body>;
    }
    // 5-step flow: each step of the page as a section, with the title the 14-step flow gives it.
    return (
      <Body>
        <Sections>
          {sections.map(id => (
            <Section key={id} aria-label={wizardPages.find(p => p.id === id)?.title || id}>
              <SectionTitle>{wizardPages.find(p => p.id === id)?.title || id}</SectionTitle>
              <SectionBody>{this.renderStep(id)}</SectionBody>
            </Section>
          ))}
        </Sections>
      </Body>
    );
  }

  renderStep(pageId: string) {
    let body = null;

    const getOptionsLoadingSkipFields = (type: "source" | "destination") => {
      const extraOptionsConfig = configLoader.config.extraOptionsApiCalls.find(
        o => {
          const provider =
            type === "source"
              ? this.props.wizardData.source &&
                this.props.wizardData.source.type
              : this.props.wizardData.target &&
                this.props.wizardData.target.type;
          return o.name === provider && o.types.find(t => t === type);
        },
      );
      let optionsLoadingRequiredFields: string[] = [];
      if (extraOptionsConfig) {
        optionsLoadingRequiredFields = extraOptionsConfig.requiredFields;
      }
      return optionsLoadingRequiredFields;
    };

    const getDefaultStorage = (): {
      value: string | null;
      busType?: string | null;
    } => {
      if (this.props.defaultStorage) {
        return this.props.defaultStorage;
      }

      if (endpointStore.storageConfigDefault) {
        const busTypeInfo = EndpointUtils.getBusTypeStorageId(
          endpointStore.storageBackends,
          endpointStore.storageConfigDefault || null,
        );
        const defaultStorage: {
          value: string | null;
          busType?: string | null;
        } = {
          value: busTypeInfo.id,
        };
        if (busTypeInfo.busType) {
          defaultStorage.busType = busTypeInfo.busType;
        }
        return defaultStorage;
      }
      return { value: null };
    };

    switch (pageId) {
      case "type":
        body = (
          <WizardType
            selected={this.props.type}
            onChange={this.props.onTypeChange}
          />
        );
        break;
      case "source":
        body = (
          <WizardEndpointList
            providers={this.getProviders("source")}
            loading={this.props.providerStore.providersLoading}
            otherEndpoint={this.props.wizardData.target}
            selectedEndpoint={this.props.wizardData.source}
            endpoints={this.props.endpointStore.endpoints}
            onChange={this.props.onSourceEndpointChange}
            onAddEndpoint={type => {
              this.props.onAddEndpoint(type, true);
            }}
          />
        );
        break;
      case "target":
        body = (
          <WizardEndpointList
            providers={this.getProviders("target")}
            loading={this.props.providerStore.providersLoading}
            otherEndpoint={this.props.wizardData.source}
            selectedEndpoint={this.props.wizardData.target}
            endpoints={this.props.endpointStore.endpoints}
            onChange={this.props.onTargetEndpointChange}
            onAddEndpoint={type => {
              this.props.onAddEndpoint(type, false);
            }}
          />
        );
        break;
      case "execute":
        body = (
          <WizardExecuteOptions
            options={[...executeOptionsWithExecuteNow, ...deploymentFields]}
            wizardType={`${this.props.type}-execute`}
            layout="page"
            data={this.props.wizardData.executeOptions}
            onChange={this.props.onTransferExecuteOptionsChange}
          />
        );
        break;
      case "guest-access":
        body = <WizardGuestAccess wizardData={this.props.wizardData} />;
        break;
      case "prerequisites":
        body = <WizardPrerequisites wizardData={this.props.wizardData} />;
        break;
      case "vms":
        body = (
          <WizardInstances
            instances={this.props.instanceStore.instances}
            instancesPerPage={this.props.instanceStore.instancesPerPage}
            chunksLoading={this.props.instanceStore.chunksLoading}
            currentPage={this.props.instanceStore.currentPage}
            searchText={this.props.instanceStore.searchText}
            loading={this.props.instanceStore.instancesLoading}
            searching={this.props.instanceStore.searching}
            searchNotFound={this.props.instanceStore.searchNotFound}
            reloading={this.props.instanceStore.reloading}
            onSearchInputChange={this.props.onInstancesSearchInputChange}
            onReloadClick={this.props.onInstancesReloadClick}
            onInstanceClick={this.props.onInstanceClick}
            onPageClick={this.props.onInstancePageClick}
            selectedInstances={this.props.wizardData.selectedInstances || []}
            hasSourceOptions={this.props.hasSourceOptions}
          />
        );
        break;
      case "source-options":
        body = (
          <WizardOptions
            ref={ref => {
              this.optionsRef = ref;
            }}
            loading={this.areOptionsLoading("source")}
            minionPools={this.props.minionPoolStore.minionPools.filter(
              m =>
                m.platform === "source" &&
                m.endpoint_id === this.props.wizardData.source?.id,
            )}
            optionsLoading={
              this.props.providerStore.sourceOptionsSecondaryLoading
            }
            optionsLoadingSkipFields={getOptionsLoadingSkipFields("source")}
            fields={this.props.providerStore.sourceSchema.filter(
              f => !BANDWIDTH_KEYS.includes(f.name),
            )}
            onChange={this.props.onSourceOptionsChange}
            data={this.props.wizardData.sourceOptions}
            useAdvancedOptions
            hasStorageMap={false}
            wizardType={`${this.props.type}-source-options`}
            layout="page"
            isSource
            dictionaryKey={`${
              this.props.wizardData.source
                ? this.props.wizardData.source.type
                : ""
            }-source`}
          />
        );
        if (this.hasBandwidthOptions && !this.areOptionsLoading("source")) {
          body = (
            <SourceOptionsColumn>
              {body}
              {this.renderBandwidth()}
            </SourceOptionsColumn>
          );
        }
        break;
      case "readiness":
        body = <WizardReadiness wizardData={this.props.wizardData} />;
        break;
      case "dest-options":
        body = (
          <WizardOptions
            ref={ref => {
              this.optionsRef = ref;
            }}
            loading={this.areOptionsLoading("destination")}
            minionPools={this.props.minionPoolStore.minionPools.filter(
              m =>
                m.platform === "destination" &&
                m.endpoint_id === this.props.wizardData.target?.id,
            )}
            optionsLoading={
              this.props.providerStore.destinationOptionsSecondaryLoading
            }
            optionsLoadingSkipFields={[
              ...getOptionsLoadingSkipFields("destination"),
              "title",
            ]}
            selectedInstances={this.props.wizardData.selectedInstances}
            showSeparatePerVm={Boolean(
              this.props.wizardData.selectedInstances &&
                this.props.wizardData.selectedInstances.length > 1,
            )}
            fields={this.props.providerStore.destinationSchema.filter(
              // RDP / SSH access is set on the guest access page, next to the logins.
              f => !REMOTE_ACCESS_KEYS.includes(f.name),
            )}
            onChange={this.props.onDestOptionsChange}
            data={this.props.wizardData.destOptions}
            useAdvancedOptions={this.state.useAdvancedOptions}
            hasStorageMap={this.props.hasStorageMap}
            storageBackends={this.props.endpointStore.storageBackends}
            wizardType={this.props.type}
            onAdvancedOptionsToggle={useAdvancedOptions => {
              this.handleAdvancedOptionsToggle(useAdvancedOptions);
            }}
            layout="page"
            dictionaryKey={`${
              this.props.wizardData.target
                ? this.props.wizardData.target.type
                : ""
            }-destination`}
            executeNowOptionsDisabled={
              !this.props.providerStore.hasExecuteNowOptions(
                this.props.wizardData.source!.type,
              )
            }
          />
        );
        break;
      case "networks":
        body = (
          <WizardNetworks
            networks={this.props.networkStore.networks}
            selectedNetworks={this.props.wizardData.networks}
            loading={this.props.networkStore.loading}
            instancesDetails={this.props.instanceStore.instancesDetails}
            loadingInstancesDetails={
              this.props.instanceStore.loadingInstancesDetails
            }
            onChange={this.props.onNetworkChange}
          />
        );
        break;
      case "storage":
        body = (
          <WizardStorage
            loading={endpointStore.storageLoading}
            onReloadClick={this.props.onStorageReloadClick}
            storageBackends={this.props.endpointStore.storageBackends}
            instancesDetails={this.props.instanceStore.instancesDetails}
            storageMap={this.props.storageMap}
            onChange={this.props.onStorageChange}
            defaultStorage={getDefaultStorage()}
            onDefaultStorageChange={this.props.onDefaultStorageChange}
            defaultStorageLayout="page"
          />
        );
        break;
      case "scripts":
        body = (
          <WizardScripts
            instances={this.props.instanceStore.instancesDetails}
            onScriptsChange={this.props.onUserScriptsChange}
            uploadedScripts={this.props.uploadedUserScripts}
            userScriptData={null}
            removedScripts={[]}
          />
        );
        break;
      case "schedule":
        body = (
          <Schedule
            disableExecutionOptions={configLoader.config.providersDisabledExecuteOptions.some(
              p => p === this.props.wizardData.source?.type,
            )}
            schedules={this.props.schedules}
            onAddScheduleClick={this.props.onAddScheduleClick}
            onChange={this.props.onScheduleChange}
            onRemove={this.props.onScheduleRemove}
            timezone={this.state.timezone}
            onTimezoneChange={timezone => {
              this.handleTimezoneChange(timezone);
            }}
            secondaryEmpty
          />
        );
        break;
      case "summary":
        body = (
          <WizardSummary
            data={this.props.wizardData}
            schedules={this.props.schedules}
            defaultStorage={this.props.defaultStorage}
            storageMap={this.props.storageMap}
            wizardType={this.props.type}
            instancesDetails={this.props.instanceStore.instancesDetails}
            sourceSchema={this.props.providerStore.sourceSchema}
            destinationSchema={this.props.providerStore.destinationSchema}
            uploadedUserScripts={this.props.uploadedUserScripts}
            minionPools={this.props.minionPoolStore.minionPools}
            executionOptions={[
              ...executeOptionsWithExecuteNow,
              ...deploymentFields,
            ]}
          />
        );
        break;
      default:
    }

    return body;
  }

  renderNavigationActions() {
    const isFirstPage = this.pageIndex === 0;
    const isLastPage = this.pageIndex === this.props.pages.length - 1;
    const hint = isLastPage
      ? `Finish creates the ${this.props.type === "replica" ? "replica" : "migration"} with these settings.`
      : "";

    return (
      <ActionBar>
        <ActionHint>{hint}</ActionHint>
        {this.props.onCancelClick && !isFirstPage ? (
          <CancelLink type="button" onClick={this.props.onCancelClick}>
            Cancel
          </CancelLink>
        ) : null}
        <Button secondary width="112px" onClick={this.props.onBackClick}>
          {isFirstPage ? "Cancel" : "Back"}
        </Button>
        {this.props.showLoadingButton ? (
          <LoadingButton width="112px">Loading</LoadingButton>
        ) : (
          <Button
            width="112px"
            onClick={() => {
              this.handleNextClick();
            }}
            disabled={this.nextButtonDisabled}
          >
            {isLastPage ? "Finish" : "Next"}
          </Button>
        )}
      </ActionBar>
    );
  }

  render() {
    if (!this.props.page) {
      return null;
    }

    return (
      <Wrapper>
        {this.renderPageTitle()}
        <Rail aria-label="Steps">
          <WizardBreadcrumbs selected={this.props.page} pages={this.props.pages} />
        </Rail>
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Card>
            {this.renderHeader()}
            {this.renderBody()}
          </Card>
          {this.renderNavigationActions()}
        </div>
      </Wrapper>
    );
  }
}

export default WizardPageContent;
